import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  Sparkles,
  MapPin,
  Ruler,
  ArrowRight,
  Loader2,
  Zap,
  Clock,
  Mail,
  User,
  CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_TYPES = [
  "2D Floor Plan",
  "3D Floor Plan",
  "Renew Existing Floor Plan",
  "Other",
];

const URGENCY = [
  { value: "within_48h", label: "Within 48 hours", icon: Zap, tint: "amber" },
  { value: "within_1w", label: "Within 1 week", icon: Clock, tint: "teal" },
];

/**
 * Portfolio "Request similar" modal — works without auth.
 * Pre-fills service_type and style based on the selected portfolio category.
 * On success, swaps to a thank-you confirmation step instead of closing.
 */
export default function PortfolioRequestModal({
  open,
  onClose,
  defaults = {},
  prefill = {},
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [sqft, setSqft] = useState("");
  const [planType, setPlanType] = useState(defaults.service_type || PLAN_TYPES[0]);
  const [urgency, setUrgency] = useState(URGENCY[0].value);
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(prefill.name || "");
    setEmail(prefill.email || "");
    setAddress("");
    setSqft("");
    setPlanType(defaults.service_type || PLAN_TYPES[0]);
    setUrgency(URGENCY[0].value);
    setComments("");
    setSubmitted(false);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!name.trim() || !email.trim() || !address.trim()) {
      toast.error("Please add your name, email, and the property address.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        property_address: address.trim(),
        estimated_sqft: sqft ? parseInt(sqft, 10) : null,
        service_type: planType,
        style: defaults.style || null,
        urgency,
        message: comments.trim() || null,
      };
      await axios.post(`${API}/leads`, payload, { withCredentials: true });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Could not submit. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center px-4 py-8 sm:py-10 bg-black/45 backdrop-blur-sm overflow-y-auto"
      data-testid="portfolio-request-modal"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="w-full max-w-lg bg-white rounded-[2rem] shadow-2xl border border-stone-200 overflow-hidden my-auto">
        {submitted ? (
          <ThankYou onClose={onClose} />
        ) : (
          <>
            <div className="flex items-start justify-between px-6 pt-6 pb-2">
              <div>
                <span className="inline-flex items-center gap-2 bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-2">
                  <Sparkles className="h-3 w-3" />
                  Request similar
                </span>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-stone-900">
                  {defaults.label ? `Request a ${defaults.label} plan` : "Request a similar plan"}
                </h2>
                <p className="text-sm text-stone-600 mt-1">
                  A few quick details — we&apos;ll take it from there.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="portfolio-request-close"
                className="h-9 w-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center flex-shrink-0 ml-3"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form
              onSubmit={onSubmit}
              className="px-6 pt-4 pb-6 space-y-4"
              data-testid="portfolio-request-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Full Name" required>
                  <IconInput
                    icon={User}
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    data-testid="portfolio-request-name"
                    required
                  />
                </Field>
                <Field label="Email" required>
                  <IconInput
                    icon={Mail}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="portfolio-request-email"
                    required
                  />
                </Field>
              </div>

              <Field label="Property Address" required>
                <IconInput
                  icon={MapPin}
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 K St NW, Washington, DC"
                  data-testid="portfolio-request-address"
                  required
                />
                <p className="mt-2 text-[11px] italic text-stone-500 pl-1">
                  *email any and all large files to redraw@dmvfp.com*
                </p>
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Estimated Sq Ft">
                  <IconInput
                    icon={Ruler}
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={sqft}
                    onChange={(e) => setSqft(e.target.value)}
                    placeholder="e.g. 1850"
                    data-testid="portfolio-request-sqft"
                  />
                </Field>
                <Field label="Floor Plan Type" required>
                  <select
                    data-testid="portfolio-request-type"
                    value={planType}
                    onChange={(e) => setPlanType(e.target.value)}
                    className="w-full bg-[#FAF7F2] border border-stone-200 rounded-full px-4 py-3 text-sm text-stone-900 focus:outline-none focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10 transition-colors cursor-pointer"
                  >
                    {PLAN_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="How soon do you need it?" required>
                <div className="grid grid-cols-2 gap-2" data-testid="portfolio-request-urgency">
                  {URGENCY.map((opt) => {
                    const Icon = opt.icon;
                    const isActive = urgency === opt.value;
                    const activeCls =
                      opt.tint === "amber"
                        ? "bg-amber-600 text-white border-amber-600"
                        : "bg-teal-700 text-white border-teal-700";
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setUrgency(opt.value)}
                        data-testid={`portfolio-request-urgency-${opt.value}`}
                        className={`inline-flex items-center justify-center gap-2 border-2 rounded-2xl px-3 py-3 text-sm font-semibold transition-colors ${
                          isActive
                            ? activeCls
                            : "bg-white text-stone-800 border-stone-200 hover:border-stone-400"
                        }`}
                      >
                        <Icon className="h-4 w-4" /> {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[11px] italic text-stone-500 pl-1">
                  *requests needed within 48 hours of contact occur a 50% rush fee*
                </p>
              </Field>

              <Field label="Comments (optional)">
                <textarea
                  data-testid="portfolio-request-comments"
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={3}
                  placeholder="Anything else we should know — access, deadlines, special requests…"
                  className="w-full bg-[#FAF7F2] border border-stone-200 rounded-2xl px-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10 transition-colors resize-none"
                />
              </Field>

              <button
                type="submit"
                disabled={submitting}
                data-testid="portfolio-request-submit"
                className="group w-full inline-flex items-center justify-center gap-3 bg-stone-900 text-white py-3.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    Submit request
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function ThankYou({ onClose }) {
  return (
    <div className="px-6 sm:px-8 py-12 text-center" data-testid="portfolio-request-thanks">
      <div className="inline-flex h-16 w-16 rounded-full bg-teal-100 items-center justify-center mb-5">
        <CheckCircle2 className="h-8 w-8 text-teal-700" strokeWidth={2} />
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight text-stone-900">
        Thank you for your interest <br />in our service.
      </h2>
      <p className="mt-5 text-stone-600 leading-relaxed max-w-md mx-auto">
        We will get back to you via email with available times{" "}
        <span className="font-semibold text-stone-900">within 1 business day.</span>
      </p>
      <button
        type="button"
        onClick={onClose}
        data-testid="portfolio-request-thanks-close"
        className="mt-8 inline-flex items-center gap-2 bg-stone-900 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors"
      >
        Close
      </button>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-stone-600 mb-1.5 block pl-1">
        {label} {required && <span className="text-teal-700">*</span>}
      </span>
      {children}
    </label>
  );
}

function IconInput({ icon: Icon, ...rest }) {
  return (
    <span className="relative block">
      <span className="absolute inset-y-0 left-3 flex items-center text-stone-400">
        <Icon className="h-4 w-4" />
      </span>
      <input
        {...rest}
        className="w-full bg-[#FAF7F2] border border-stone-200 rounded-full pl-10 pr-4 py-3 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-teal-700 focus:bg-white focus:ring-4 focus:ring-teal-700/10 transition-colors"
      />
    </span>
  );
}
